import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import Navbar from '../home/components/Navbar';
import Footer from '../home/components/Footer';

export default function FrenchDoors() {
  const { t } = useTranslation('common');
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);

  const galleryImages = [
    {
      url: 'https://public.readdy.ai/ai/img_res/5e837fcbb9941540fa4be67bea77aefc.jpg',
      alt: 'White French Doors Interior'
    },
    {
      url: 'https://readdy.ai/api/search-image?query=classic%20wooden%20french%20doors%20with%20decorative%20glass%20panels%20traditional%20style%20elegant%20hardware%20brass%20handles%20warm%20wood%20finish&width=800&height=600&seq=fd002&orientation=landscape',
      alt: 'Wooden French Doors'
    },
    {
      url: 'https://readdy.ai/api/search-image?query=modern%20black%20french%20doors%20with%20full%20glass%20panels%20contemporary%20design%20sleek%20frames%20opening%20to%20outdoor%20patio%20minimalist%20style&width=800&height=600&seq=fd003&orientation=landscape',
      alt: 'Black French Doors'
    },
    {
      url: 'https://readdy.ai/api/search-image?query=french%20doors%20with%20divided%20light%20grilles%20colonial%20style%20white%20painted%20finish%20traditional%20grid%20pattern%20elegant%20symmetrical%20design&width=800&height=600&seq=fd004&orientation=landscape',
      alt: 'French Doors with Grilles'
    },
    {
      url: 'https://readdy.ai/api/search-image?query=exterior%20french%20doors%20opening%20to%20garden%20patio%20beautiful%20outdoor%20view%20natural%20wood%20finish%20double%20door%20configuration&width=800&height=600&seq=fd005&orientation=landscape',
      alt: 'Exterior French Doors'
    },
    {
      url: 'https://readdy.ai/api/search-image?query=french%20doors%20with%20sidelights%20entry%20door%20configuration%20elegant%20glass%20panels%20symmetrical%20design%20bright%20natural%20lighting&width=800&height=600&seq=fd006&orientation=landscape',
      alt: 'French Doors with Sidelights'
    },
    {
      url: 'https://readdy.ai/api/search-image?query=sliding%20french%20doors%20modern%20space%20saving%20design%20large%20glass%20panels%20contemporary%20hardware%20smooth%20operation%20mechanism&width=800&height=600&seq=fd007&orientation=landscape',
      alt: 'Sliding French Doors'
    },
    {
      url: 'https://readdy.ai/api/search-image?query=french%20doors%20with%20transom%20window%20above%20elegant%20entry%20configuration%20decorative%20glass%20panels%20classic%20architectural%20detail&width=800&height=600&seq=fd008&orientation=landscape',
      alt: 'French Doors with Transom'
    }
  ];

  const openLightbox = (index: number) => {
    setCurrentImageIndex(index);
    setLightboxOpen(true);
  };

  const closeLightbox = () => {
    setLightboxOpen(false);
  };

  const goToPrevious = () => {
    setCurrentImageIndex((prev) => (prev === 0 ? galleryImages.length - 1 : prev - 1));
  };

  const goToNext = () => {
    setCurrentImageIndex((prev) => (prev === galleryImages.length - 1 ? 0 : prev + 1));
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'ArrowLeft') goToPrevious();
    if (e.key === 'ArrowRight') goToNext();
    if (e.key === 'Escape') closeLightbox();
  };

  return (
    <div className="min-h-screen bg-white">
      <Navbar />
      
      {/* Hero Section */}
      <section className="relative h-[500px] flex items-center justify-center overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://readdy.ai/api/search-image?query=elegant%20french%20doors%20with%20glass%20panels%20opening%20to%20beautiful%20outdoor%20space%20natural%20light%20streaming%20through%20classic%20architectural%20design%20sophisticated%20home%20entrance&width=1920&height=500&seq=fdhero&orientation=landscape"
            alt="French Doors Hero"
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/30 to-black/40"></div>
        </div>
        <div className="relative z-10 text-center text-white px-4 max-w-4xl mx-auto">
          <h1 className="text-5xl md:text-6xl font-bold mb-6">{t('frenchDoors.hero.title')}</h1>
          <p className="text-xl md:text-2xl mb-8">{t('frenchDoors.hero.subtitle')}</p>
          <a
            href="/#contact"
            className="inline-block bg-teal-500 hover:bg-teal-600 text-white px-8 py-4 rounded-lg text-lg font-semibold transition-colors whitespace-nowrap cursor-pointer"
          >
            {t('frenchDoors.hero.cta')}
          </a>
        </div>
      </section>

      {/* Overview Section */}
      <section className="py-20 px-4">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-6">{t('frenchDoors.overview.title')}</h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto leading-relaxed">
              {t('frenchDoors.overview.description')}
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            <div className="text-center p-6">
              <div className="w-16 h-16 bg-teal-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="ri-door-open-line text-3xl text-teal-600"></i>
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-3">{t('frenchDoors.overview.feature1.title')}</h3>
              <p className="text-gray-600">{t('frenchDoors.overview.feature1.description')}</p>
            </div>

            <div className="text-center p-6">
              <div className="w-16 h-16 bg-teal-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="ri-sun-line text-3xl text-teal-600"></i>
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-3">{t('frenchDoors.overview.feature2.title')}</h3>
              <p className="text-gray-600">{t('frenchDoors.overview.feature2.description')}</p>
            </div>

            <div className="text-center p-6">
              <div className="w-16 h-16 bg-teal-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="ri-palette-line text-3xl text-teal-600"></i>
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-3">{t('frenchDoors.overview.feature3.title')}</h3>
              <p className="text-gray-600">{t('frenchDoors.overview.feature3.description')}</p>
            </div>

            <div className="text-center p-6">
              <div className="w-16 h-16 bg-teal-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="ri-shield-check-line text-3xl text-teal-600"></i>
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-3">{t('frenchDoors.overview.feature4.title')}</h3>
              <p className="text-gray-600">{t('frenchDoors.overview.feature4.description')}</p>
            </div>
          </div>
        </div>
      </section>

      {/* Styles Section */}
      <section className="py-20 px-4 bg-gray-50">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-6">{t('frenchDoors.styles.title')}</h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              {t('frenchDoors.styles.subtitle')}
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {[1, 2, 3, 4, 5, 6].map((num) => (
              <div key={num} className="bg-white rounded-lg p-8 shadow-sm hover:shadow-md transition-shadow">
                <div className="w-14 h-14 bg-teal-100 rounded-lg flex items-center justify-center mb-6">
                  <i className="ri-door-line text-2xl text-teal-600"></i>
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-3">
                  {t(`frenchDoors.styles.style${num}.title`)}
                </h3>
                <p className="text-gray-600 leading-relaxed">
                  {t(`frenchDoors.styles.style${num}.description`)}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Photo Gallery Section */}
      <section className="py-20 px-4">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-6">{t('frenchDoors.gallery.title')}</h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              {t('frenchDoors.gallery.subtitle')}
            </p>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {galleryImages.map((image, index) => (
              <div
                key={index}
                className="relative aspect-[4/3] overflow-hidden rounded-lg cursor-pointer group"
                onClick={() => openLightbox(index)}
              >
                <img
                  src={image.url}
                  alt={image.alt}
                  className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-110"
                />
                <div className="absolute inset-0 bg-black/0 group-hover:bg-black/30 transition-colors duration-300 flex items-center justify-center">
                  <i className="ri-zoom-in-line text-white text-3xl opacity-0 group-hover:opacity-100 transition-opacity duration-300"></i>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Lightbox Modal */}
      {lightboxOpen && (
        <div
          className="fixed inset-0 bg-black/95 z-50 flex items-center justify-center p-4"
          onClick={closeLightbox}
          onKeyDown={handleKeyDown}
          tabIndex={0}
        >
          <button
            onClick={closeLightbox}
            className="absolute top-4 right-4 text-white hover:text-gray-300 transition-colors cursor-pointer"
            aria-label="Close"
          >
            <i className="ri-close-line text-4xl"></i>
          </button>

          <button
            onClick={(e) => {
              e.stopPropagation();
              goToPrevious();
            }}
            className="absolute left-4 text-white hover:text-gray-300 transition-colors cursor-pointer"
            aria-label="Previous"
          >
            <i className="ri-arrow-left-s-line text-5xl"></i>
          </button>

          <button
            onClick={(e) => {
              e.stopPropagation();
              goToNext();
            }}
            className="absolute right-4 text-white hover:text-gray-300 transition-colors cursor-pointer"
            aria-label="Next"
          >
            <i className="ri-arrow-right-s-line text-5xl"></i>
          </button>

          <div className="max-w-6xl w-full" onClick={(e) => e.stopPropagation()}>
            <img
              src={galleryImages[currentImageIndex].url}
              alt={galleryImages[currentImageIndex].alt}
              className="w-full h-auto max-h-[85vh] object-contain rounded-lg"
            />
            <div className="text-center mt-4 text-white text-lg">
              {currentImageIndex + 1} / {galleryImages.length}
            </div>
          </div>
        </div>
      )}

      {/* Benefits Section */}
      <section className="py-20 px-4 bg-gray-50">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-6">{t('frenchDoors.benefits.title')}</h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              {t('frenchDoors.benefits.subtitle')}
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {[1, 2, 3, 4, 5, 6].map((num) => (
              <div key={num} className="flex gap-4">
                <div className="flex-shrink-0">
                  <div className="w-12 h-12 bg-teal-500 rounded-lg flex items-center justify-center">
                    <i className="ri-check-line text-2xl text-white"></i>
                  </div>
                </div>
                <div>
                  <h3 className="text-xl font-semibold text-gray-900 mb-2">
                    {t(`frenchDoors.benefits.benefit${num}.title`)}
                  </h3>
                  <p className="text-gray-600 leading-relaxed">
                    {t(`frenchDoors.benefits.benefit${num}.description`)}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-4">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-4xl font-bold text-gray-900 mb-6">{t('frenchDoors.cta.title')}</h2>
          <p className="text-xl text-gray-600 mb-8">
            {t('frenchDoors.cta.description')}
          </p>
          <a
            href="/#contact"
            className="inline-block bg-teal-500 hover:bg-teal-600 text-white px-10 py-4 rounded-lg text-lg font-semibold transition-colors whitespace-nowrap cursor-pointer"
          >
            {t('frenchDoors.cta.button')}
          </a>
        </div>
      </section>

      <Footer />
    </div>
  );
}